import { useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { FluentProvider } from '@fluentui/react-components';
import { router } from './router';
import { AuthProvider } from '@/shared/providers/AuthProvider';
import { DevRoleSwitcher } from '@/shared/providers/DevRoleSwitcher';
import { ThemeProvider, useThemeMode } from '@/shared/providers/ThemeProvider';
import { utahHealthTheme, utahHealthDarkTheme } from '@/theme/utahHealthTheme';
import { useGlobalStyles } from '@/shared/styles/globalStyles';

// QueryClient with sensible defaults per stack-conventions.
// staleTime 5 min, retry once, no refetch on window focus.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 1000 * 60 * 5, retry: 1, refetchOnWindowFocus: false },
    mutations: { retry: 0 },
  },
});

// NOTE: never pass a layout className to <FluentProvider> — Fluent copies the
// provider's classes onto every popup portal node (Dropdown/Menu/Dialog
// surfaces), so layout styles there turn portals into full-viewport overlays
// that hide popups and block clicks. The app-shell layout for the provider
// element lives in shared/styles/globalStyles.ts under `#root > .fui-FluentProvider`.

/**
 * Inner component — must be rendered inside <ThemeProvider> so that
 * useThemeMode() can access the context. Calls useGlobalStyles() to inject
 * static global CSS, selects the active Fluent theme, and keeps the <body>
 * background in sync with the theme's page-background token.
 */
function ThemedApp() {
  useGlobalStyles();
  const { mode } = useThemeMode();
  const theme = mode === 'dark' ? utahHealthDarkTheme : utahHealthTheme;

  // The page background lives on <body> (outside FluentProvider), so keep it
  // in sync with the active theme's page-background token.
  useEffect(() => {
    document.body.style.backgroundColor = theme.colorNeutralBackground3;
  }, [theme]);

  return (
    <FluentProvider theme={theme}>
      <RouterProvider router={router} />
      <DevRoleSwitcher />
    </FluentProvider>
  );
}

/**
 * Provider order (outside-in):
 *   QueryClientProvider → AuthProvider → ThemeProvider → FluentProvider → RouterProvider
 *
 * ThemeProvider must wrap ThemedApp because useThemeMode() is called inside it.
 * DevRoleSwitcher self-hides when devAuthEnabled is false, so it is safe to
 * mount unconditionally. In a production bundle it is tree-shaken away entirely
 * (import.meta.env.DEV is false).
 */
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeProvider>
          <ThemedApp />
        </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
